/**
 * Labstep
 *
 * @module components/Metadata/Action/Create/Modal/Wizard/MetadataActionCreateModalWizardStepAdditionalSettings
 * @desc Step 4: Additional Settings - Configure checkboxes and submit the form
 */

import { FIELD_METADATA_IS_REQUIRED } from 'labstep-web/components/Metadata/Form/fields';
import { preSubmit } from 'labstep-web/components/Metadata/Form/preSubmit';
import { FieldType } from 'labstep-web/constants/form';
import { FormCreateContainer } from 'labstep-web/core/Form/Create';
import type {
  IFieldProps,
  IValues,
} from 'labstep-web/core/Form/Reusable/types';
import { ModalWizard } from 'labstep-web/core/Modal/Wizard';
import type { APICallOptions, APIResponse } from 'labstep-web/models';
import { Metadata } from 'labstep-web/models/metadata';
import { enhanceOptions } from 'labstep-web/services/utils.service';
import React, { useMemo } from 'react';

interface MetadataActionCreateModalWizardStepAdditionalSettingsProps {
  entity: any;
  isTemplate?: boolean;
  formValues: IValues;
  body: any;
  onSuccess: APICallOptions['onSuccess'];
  stepIndex: number;
}

export const MetadataActionCreateModalWizardStepAdditionalSettings: React.FC<
  MetadataActionCreateModalWizardStepAdditionalSettingsProps
> = ({
  entity,
  isTemplate,
  formValues,
  body,
  onSuccess,
  stepIndex,
}) => {
  const formRef = React.useRef<{ submit: VoidFunction }>(null);
  const goForwardRef = React.useRef<(() => void) | null>(null);

  const fields: IFieldProps[] = useMemo(() => {
    const fieldArray: IFieldProps[] = [
      {
        name: 'is_multiple_entity_relations_allowed',
        fieldLabel: 'Allow multiple resources to be selected',
        fieldType: FieldType.Checkbox,
      } as IFieldProps,
      {
        name: 'is_auto_populate_enabled',
        fieldLabel: 'Autopopulate from experiment',
        fieldType: FieldType.Checkbox,
      } as IFieldProps,
    ];

    if (isTemplate) {
      fieldArray.push(FIELD_METADATA_IS_REQUIRED as IFieldProps);
    }

    return fieldArray;
  }, [isTemplate]);

  const handleSuccess = (response: APIResponse) => {
    onSuccess?.(response);
    if (goForwardRef.current) {
      goForwardRef.current();
    }
  };

  return (
    <ModalWizard.Step
      index={stepIndex}
      description="Configure additional options"
      continueLabel="Add Field"
      onContinue={({ goForward }) => {
        // Store goForward for use after form submission
        goForwardRef.current = goForward;
        // Trigger form submission
        if (formRef.current) {
          formRef.current.submit();
        }
      }}
      hideHeader
    >
      <FormCreateContainer
        entityName={Metadata.entityName}
        parentId={entity.metadata_thread.idAttr}
        parentName={entity.metadata_thread.entityName}
        autoFocus
        body={body}
        preSubmit={preSubmit}
        defaultValues={formValues}
        fields={fields}
        formRef={formRef}
        submitButton={false}
        options={enhanceOptions({
          options: { onSuccess: handleSuccess },
          toast: true,
        })}
      />
    </ModalWizard.Step>
  );
};
